import { put, call, all } from 'redux-saga/effects'
import axios from 'axios'
import _ from 'lodash'

import {
  INIT_SAGAS_START,
  INIT_SAGAS_ERROR,
  INIT_SAGAS_FINALLY,
  LOGIN_ERROR,
  LOGIN_SET_PROFILE,
  SET_COMPANY,
  SET_FORMS_AND_DEPENDENCIES,
  POINTS_SET_POINTS,
  SET_CLASIFICATIONS,
  SET_TAGS,
  SET_ADDONS,
  SET_WIDGETS,
} from '../../actions'
import {
  GET_POINTS,
} from '../Points/graphql/queries'
import apolloClient from '../../apolloClient'

export function* GetUserAndGroups({ history }) {
  const userResponse = yield call(axios.get, '/api/users/me')

  if (!userResponse.data.active || !userResponse.data.valid) {
    yield put(LOGIN_ERROR('Tu cuenta no se encuentra activa'))
    yield put(history.replace({ pathname: '/login' }))

    return { error: true }
  }

  yield put(LOGIN_SET_PROFILE(userResponse.data))
  const usersGroupsResponse = yield call(axios.get, '/api/users_groups', {
    params: {
      q: {
        user_id: userResponse.data._id,
        valid: true,
      },
    },
  })

  if (!usersGroupsResponse.data.length) {
    yield put(LOGIN_ERROR('Sólo puedes acceder desde la app móvil'))
    yield call(history.replace, '/login')

    return { error: true }
  }

  const groupsIds = usersGroupsResponse.data.map(x => x.group_id)
  const groupsResponse = yield call(axios.get, '/api/groups', {
    params: {
      q: {
        _id: { $in: groupsIds },
        valid: true,
      },
    },
  })

  if (!groupsResponse.data.length) {
    yield put(LOGIN_ERROR('Acceso solo para administradores'))
    yield call(history.replace, '/login')

    return { error: true }
  }

  return {
    groups: groupsResponse.data,
    groupsIds,
    profile: userResponse.data,
  }
}

export function* GetCompany({ history, groups }) {
  const { company_id: companyId } = (_.head(groups || []) || {})

  const { data: company } = yield call(axios.get, `/api/companies/${companyId}`)

  const unexpiredCompany = new Date(company.expire) >= new Date()
    ? company
    : null

  if (!unexpiredCompany) {
    yield put(LOGIN_ERROR('La cuenta de empresa ha expirado.'))

    return yield call(history.replace, '/login')
  }

  yield put(SET_COMPANY(unexpiredCompany))

  return { company: unexpiredCompany }
}

export function* GetForms({ company, profile }) {
  const [
    { data: forms },
    { data: stages },
    { data: funnels },
  ] = yield all([
    call(axios.get, 'api/forms', {
      params: {
        q: {
          company_id: company._id,
          active: true,
          valid: true,
        },
      },
    }),
    call(axios.get, 'api/stages', {
      params: {
        q: {
          company_id: company._id,
          valid: true,
        },
      },
    }),
    call(axios.get, 'api/funnels', {
      params: {
        q: {
          company_id: company._id,
          valid: true,
        },
      },
    }),
  ])
  const formIds = forms.map(x => x._id)
  const [
    { data: formsPoints },
    { data: funnelsStages },
    { data: questionsGroups },
  ] = yield all([
    call(axios.get, '/api/forms_points', {
      params: {
        q: {
          form_id: { $in: formIds },
          created_by: profile._id,
          valid: true,
        },
      },
    }),
    call(axios.get, '/api/funnels_stages', {
      params: {
        q: {
          funnel_id: { $in: funnels.map(x => x._id) },
          valid: true,
        },
      },
    }),
    call(axios.get, '/api/questions_groups', {
      params: {
        q: {
          form_id: { $in: formIds },
          valid: true,
        },
      },
    }),
  ])

  const questionGroupIds = questionsGroups.map(x => x._id)
  const questionGroupIdChunk = _.chunk(questionGroupIds, 100)
  const questionsResponse = yield all(questionGroupIdChunk.map(ids => call(
    axios.get, '/api/questions', {
      params: {
        q: {
          question_group_id: { $in: ids },
          valid: true,
        },
      },
    },
  )))

  const questionsData = questionsResponse.map(questionResponse => questionResponse.data)
  const flattenQuestions = _.flattenDeep(questionsData)
  const questions = _.uniqBy(flattenQuestions, '_id')

  yield put(SET_FORMS_AND_DEPENDENCIES({
    forms,
    stages,
    funnels,
    funnelsStages,
    questionsGroups,
    questions,
    formsPoints,
  }))
}

export function* GetPoints(user) {
  const { data: usersPoints } = yield call(
    axios.get,
    '/api/users_points', {
      params: {
        q: {
          user_id: user._id,
          active: true,
          valid: true,
        },
      },
    },
  )
  const { data: { getPoints } } = yield call(apolloClient.query, {
    query: GET_POINTS,
    variables: {
      q: {
        active: true,
        owner: 'company',
        valid: true,
        _id: { $in: usersPoints.map(up => up.point_id) },
      },
    },
    fetchPolicy: 'no-cache',
  })

  yield put(POINTS_SET_POINTS(getPoints))
}

export function* GetClasificationsAndTags({ company }) {
  const [
    { data: clasifications },
    { data: tags },
  ] = yield all([
    call(
      axios.get,
      '/api/clasifications',
      {
        params: {
          q: {
            company_id: company._id,
            valid: true,
          },
        },
      },
    ),
    call(
      axios.get,
      '/api/tags',
      {
        params: {
          q: {
            company_id: company._id,
            valid: true,
          },
        },
      },
    ),
  ])

  yield put(SET_CLASIFICATIONS(clasifications))
  yield put(SET_TAGS(tags))
}

export function* GetAddons({ company }) {
  const { data: addonsCompany } = yield call(axios.get, '/api/addon_company', {
    params: { q: { company_id: company._id, valid: true } },
  })

  const addonsIds = addonsCompany.map(x => x.addon_id)
  const addonsCompanyIds = addonsCompany.map(x => x._id)

  const { data: addons } = yield call(axios.get, '/api/addons', {
    params: { q: { _id: { $in: addonsIds } } },
  })

  const { data: addonsConfig } = yield call(axios.get, '/api/addon_config', {
    params: { q: { addon_id: { $in: addonsIds } } },
  })

  const addonsConfigIds = addonsConfig.map(x => x._id)

  const { data: addonsConfigValues } = yield call(axios.get, '/api/addon_config_values', {
    params: {
      q: {
        addon_config_id: { $in: addonsConfigIds },
        addon_company_id: { $in: addonsCompanyIds },
      },
    },
  })

  yield put(SET_ADDONS({ addons, addonsConfig, addonsConfigValues }))

  return {
    addonsIds,
  }
}

export function* GetCustomMetrics({ company, profile, addonsIds }) {
  const customMetrics = yield call(axios.get, '/api/custom_metrics', {
    params: {
      q: {
        company_id: company._id,
        addon_id: { $in: addonsIds },
        user_id: profile._id,
        active: true,
        valid: true,
      },
    },
  })

  yield put(SET_WIDGETS(customMetrics.data))
}

export default (history, localStorage) => function* ({ payload: { token } }) {
  try {
    yield put(INIT_SAGAS_START())
    axios.defaults.headers.common.Authorization = `Bearer ${token}`
    localStorage.setItem('token', token)
    history.replace({ pathname: '/' })
    const {
      groups,
      profile,
    } = yield call(GetUserAndGroups, { history })
    const { company } = yield call(GetCompany, { history, groups })
    yield call(GetForms, { company, profile })
    yield call(GetPoints, profile)
    yield call(GetClasificationsAndTags, { company })
    const { addonsIds } = yield call(GetAddons, { company })
    yield call(GetCustomMetrics, { company, profile, addonsIds })
    history.replace({ pathname: '/dashboard' })
  } catch (error) {
    yield put(INIT_SAGAS_ERROR(error))
    history.replace({ pathname: '/login' })
  } finally {
    yield put(INIT_SAGAS_FINALLY())
  }
}
